import { useState } from 'react'
import type { Dropped, LineId } from '../../contract'
import { REASON_HELP } from '../format'

interface Props {
  dropped: Dropped[]
  ran: boolean
  onOpenLine: (id: LineId) => void
  /** ids that exist in the corpus, so an offered citation can link to the reader */
  knownTranscripts: Set<string>
  focusClaim?: string | null
}

/** Every claim the verifier refused, with the reason and exactly what the model offered as evidence. */
export function DroppedPanel({ dropped, ran, onOpenLine, knownTranscripts, focusClaim }: Props) {
  const [open, setOpen] = useState<Set<number>>(new Set())
  const toggle = (i: number) => setOpen((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n })

  return (
    <section className="dropped" data-testid="dropped-panel" aria-label="Dropped claims">
      <header className="dropped__head">
        <h3 className="dropped__title">Dropped <span className="count" data-testid="dropped-count">{dropped.length}</span></h3>
        <p className="dropped__rule">No verbatim quote, no claim. Everything the model tried to say and could not back up lands here instead of disappearing.</p>
      </header>
      {!ran && <p className="empty">Run first. Anything the model cannot prove will show up here, not in the themes.</p>}
      {ran && dropped.length === 0 && <p className="empty">Nothing dropped this run. Every claim had a receipt.</p>}
      <ul className="dropped__list">
        {dropped.map((d, i) => {
          const isOpen = open.has(i) || focusClaim === d.claim
          return (
            <li key={i} className={`drop${isOpen ? ' drop--open' : ''}`} data-testid="dropped-item" data-reason={d.reason}>
              <button className="drop__row" onClick={() => toggle(i)} aria-expanded={isOpen}>
                <span className="drop__claim">{d.claim}</span>
                <span className="reason" data-reason={d.reason}>{d.reason}</span>
              </button>
              {isOpen && (
                <div className="drop__body" data-testid="dropped-detail">
                  <p className="drop__why">{REASON_HELP[d.reason] ?? d.reason}</p>
                  <div className="drop__offered">
                    <span className="drop__offered-label">Offered as evidence</span>
                    {d.offered.length === 0 && <span className="drop__none">nothing — no citations at all</span>}
                    {d.offered.map((c, j) => {
                      const tid = c.lineId.slice(0, c.lineId.lastIndexOf(':'))
                      const linkable = knownTranscripts.has(tid) && d.reason !== 'line not found'
                      return (
                        <div key={j} className="offered">
                          <span className="mono offered__id">{c.lineId}</span>
                          <q className="offered__quote">{c.quote || '(empty quote)'}</q>
                          {linkable && <button className="linkbtn" onClick={() => onOpenLine(c.lineId)}>open line</button>}
                          {d.reason === 'line not found' && <span className="offered__missing">does not exist</span>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
